import type {Secure} from '../helpers/userSecure'
import type {
  AdvertiseDetail,
  AppStorage,
  Filter,
  FilterListItem,
  ProductCondition,
  Unknown,
} from '../index'
import type {FilterMapper} from './filterMapper'
import {Endpoint, MakeRequest, RestResponse} from './request'

export interface SavedFilterAddResponse {
  readonly id: number
}

export interface SavedFilterResponse {
  published: any
  priceMax: string | Unknown
  priceMin: string | Unknown
  cityId: any
  regionId: any
  countryId: any
  condition: ProductCondition
  readonly categoryID: number
  readonly createdAtTimeStamp: number
  readonly distanceMax: number
  readonly distanceMin: number
  readonly favorites: [{hash: 'eDxAE8t5'}]
  readonly fields: [
    {
      fieldID: number
      multiselectID: number
      value: string
    },
  ]
  readonly from: 0
  readonly hash: string
  readonly id: number
  readonly isNew: boolean
  readonly isSubscription: boolean
  readonly isWithPhoto: boolean
  readonly latitude: number
  readonly longitude: number
  readonly notification: {
    partOfDay: 'immediately' | 'day' | 'morning' | 'evening' | 'once'
    types: {
      isPushEnabled: boolean
      isEmailEnabled: boolean
    }
  }
  readonly notificationTime: number
  readonly showOnlyFavorite: boolean
  readonly sortDirection: string
  readonly sortField: string
  readonly title: string
  readonly to: number
}

export class FilterApi {
  constructor(
    private isDev: boolean,
    private makeRequest: MakeRequest,
    private storage: AppStorage,
    private secure: Secure,
    private filterMapper: FilterMapper,
  ) {}

  subscribeOnFilter = async (
    name: string,
    filter: Filter,
    type: number = 1,
  ) => {
    const uid = this.storage.userHash
    // const phone = this.storage.phone ?? ''
    const {latitude, longitude} = this.storage.location
    const secure = this.secure.createUserSecure()

    const prepared = this.filterMapper.prepareAdsFilterQuery(filter)

    const params = {
      hash: uid,
      secur: secure,
      type: type,
      // phone: phone,
      categoryId: filter.categoryId,
      title: name,
      latitude,
      longitude,
    }

    const sendParams = {...prepared, ...params}
    const response = await this.makeRequest.request<any>({
      api: Endpoint.addFilter,
      method: 'POST',
      body: sendParams,
    })
    return response
  }

  restSubscribeOnFilter = async (
    name: string,
    filter: Filter,
    type: number = 1,
  ) => {
    const uid = this.storage.userHash
    // const phone = this.storage.phone ?? ''
    const {latitude, longitude} = this.storage.location
    const secure = this.secure.createUserSecure()

    const prepared = this.filterMapper.prepareAdsFilterQuery(filter)

    if (this.isDev) console.log(JSON.stringify(prepared, undefined, 2))

    const params = {
      hash: uid,
      secur: secure,
      type: type,
      // phone: phone,
      //cat_id: filter.categoryId,
      title: name,
      latitude,
      longitude,
    }

    const sendParams = {...prepared, ...params}
    const response = await this.makeRequest.request<any>({
      api: 'v1/search_filters_add_new.php',
      method: 'POST',
      body: sendParams,
    })
    return response
  }

  restEditFilter = async (
    id: string,
    name: string,
    filter: Filter,
    type: number = 1,
  ) => {
    const uid = this.storage.userHash
    // const phone = this.storage.phone ?? ''
    const {latitude, longitude} = this.storage.location
    const secure = this.secure.createUserSecure()

    const prepared = this.filterMapper.prepareAdsFilterQuery(filter)
    if (this.isDev) console.log(JSON.stringify(prepared, undefined, 2))

    const params = {
      hash: uid,
      secur: secure,
      type: type,
      // phone: phone,
      cat_id: filter.categoryId,
      filter_id: id,
      title: name,
      latitude,
      longitude,
    }

    const sendParams = {...prepared, ...params}
    return this.makeRequest.request({
      api: Endpoint.editFilter,
      method: 'POST',
      body: sendParams,
    })
  }

  restDeleteFilter = async (id: string): Promise<RestResponse<boolean>> => {
    const uid = this.storage.userHash
    // const phone = this.storage.phone ?? ''
    const secure = this.secure.createUserSecure()

    let params = {
      hash: uid,
      secur: secure,
      type: 0,
      // phone: phone,
      filter_id: id,
    }
    return this.makeRequest.request({api: Endpoint.deleteFilter, body: params})
  }

  fetchSavedFilters = async (): Promise<
    RestResponse<Array<FilterListItem>>
  > => {
    const uid = this.storage.userHash
    // const phone = this.storage.phone ?? ''
    const secure = this.secure.createUserSecure()
    let params = {
      hash: uid,
      secur: secure,
      // num: phone,
    }

    const response = await this.makeRequest.request<Array<any>>({
      api: 'v1/search_filters_get_new.php',
      body: params,
    })
    if (response.error) return {error: response.error}
    if (!response.result) return {}
    const filters = response.result.map((r) => {
      const f = this.filterMapper.mapRestFilterToApp(r)
      return {
        filter: f,
      }
    })
    return {result: filters}
  }

  // region Сохраненные фильтры/поиски

  /**
   * Запрос на сохранение поиска:
   *   v2/products/search_filters/add */
  savedFilterSave = async (
    name: string,
    filter: Filter,
  ): Promise<RestResponse<SavedFilterAddResponse>> => {
    // const uid = this.storage.userHash
    // const phone = this.storage.phone ?? ''
    // const secure = this.secure.createUserSecure()
    const {latitude, longitude} = this.storage.location

    const params = this.filterMapper.mapFilterToV2Api(filter, {
      title: name,
      latitude,
      longitude,
    })
    if (this.isDev) console.log(JSON.stringify(params, undefined, 2))

    try {
      const response = await this.makeRequest.newRequest<SavedFilterAddResponse>(
        {
          endpoint: `v2/products/search_filters/add`,
          data: params,
        },
        // dataResponseConverter,
      )
      return response
    } catch (e) {
      console.error('savedFilterSave', e)
      return {error: 'internal_server_error', status: 500}
    }
  }

  /**
   * Запрос на список сохраненных поисков:
   * v2/products/search_filters/get
   */
  savedFilters = async (): Promise<RestResponse<FilterListItem[]>> => {
    try {
      const response = await this.makeRequest.newRequest<SavedFilterResponse[]>(
        {
          endpoint: `v2/products/search_filters/get`,
        },
        // dataResponseConverter,
      )
      const result: FilterListItem[] = (response.result ?? []).map(
        (remoteFilter) => ({
          filter: this.filterMapper.mapFilterRestV2ToModels(remoteFilter),
        }),
      )
      return {result}
    } catch (e) {
      console.error('savedFilters', e)
      return {error: 'internal_server_error', status: 500}
    }
  }

  /**
   * Запрос на редактирование фильтра:
   * v2/products/search_filters/edit */
  savedFilterUpdate = async (): Promise<RestResponse<AdvertiseDetail>> => {
    try {
      return {error: 'internal_server_error', status: 500}
    } catch (e) {
      console.error('savedFilterUpdate', e)
      return {error: 'internal_server_error', status: 500}
    }
  }

  /**
   * Запрос на удаление поиска:
   * v2/products/search_filters/delete */
  savedFilterDelete = async (): Promise<RestResponse<AdvertiseDetail>> => {
    try {
      return {error: 'internal_server_error', status: 500}
    } catch (e) {
      console.error('savedFilterDelete', e)
      return {error: 'internal_server_error', status: 500}
    }
  }

  // endregion
}
